import { useQuery } from '@apollo/client'
import { withTopbarProps } from '@jusbrasil/topbar'
import { withApollo } from '@jusbrasil-web/core-apollo'
import {
  buildExperimentContext,
  buildMetricsProperties,
  DocSearch,
  DocSearchElement,
  DocumentStatus,
  Docview,
  DOCVIEW_MOTION_JUSIA_FLAG,
  FEATURE_CODES,
  getDocumentStatusCode,
  isGoogleBotAgent,
  JUSBRASIL_HOTJAR_SITE_ID,
  MetricsProperties,
  printStyles,
  RecommendedDocuments,
  redirectDocumentUrl,
  shouldConsumeViewCredits,
  useConsumeCredits,
  useDwellTime,
  useMetrics,
  ViewLockContext,
  ViewLockWrapper,
} from '@jusbrasil-web/docview/core'
import { SetNonNullableQueryRootProps } from '@jusbrasil-web/shared-apollo-client/utilities'
import { AppBar } from '@jusbrasil-web/shared-appbar'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import {
  COMPONENT_CLASS,
  FEATURE_CLASS,
  FEATURE_NAME,
  GA4FeatureUseEvent,
  REQUIRED_USER_STATUS,
  SELECTED_OPTION,
} from '@jusbrasil-web/shared-ga4-events'
import { withGrowthBook } from '@jusbrasil-web/shared-growthbook'
import { hotjarEvent, HotjarScript } from '@jusbrasil-web/shared-hotjar-script'
import { ServerSidePersistentAlertBanner } from '@jusbrasil-web/shared-persistent-alert-banner'
import { withUserSession } from '@jusbrasil-web/shared-user-session'
import classNames from 'classnames'
import { GetServerSideProps, InferGetServerSidePropsType } from 'next'
import { useEffect, useRef } from 'react'
import UAParser from 'ua-parser-js'

import {
  JusIaVariant,
  LawsuitSubjects,
  MotionAppbar,
  MotionBreadcrumb,
  MotionDocument,
  MotionHeader,
  MotionMetaTags,
  MotionSidebar,
  MotionToolbar,
  MotionTooltip,
} from '../../components'
import { getActiveExperiments } from '../../experiment'
import { extractMotionUrl, MotionTextProcessor } from '../../utils'
import { MotionRootQuery, MotionRootQueryVariables } from './__generated__/query.graphql'
import styles from './motion.module.scss'
import { MotionRoot } from './query'

/* eslint-disable-next-line @typescript-eslint/no-empty-interface */
export interface MotionPageProps {
  variables: MotionRootQueryVariables
  metricsProperties: MetricsProperties
  disableTouchFeatures?: boolean
  httpError?: true
  shouldConsumeViewCredits?: boolean
  isGoogleBotAgent?: boolean
  processedText?: string
  jusiaVariant?: JusIaVariant
}

type MotionRootQueryPage = SetNonNullableQueryRootProps<MotionRootQuery, 'document'>

const eventData = {
  app_id: 'web-docview',
  from_component: 'docview/MotionPage',
}

export function MotionPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const {
    disableTouchFeatures,
    httpError,
    metricsProperties,
    shouldConsumeViewCredits,
    variables,
    isGoogleBotAgent,
    processedText,
    jusiaVariant,
  } = props

  const contentRef = useRef<HTMLDivElement>(null)
  const docSearchRef = useRef<DocSearchElement>(null)

  const { data } = useQuery<MotionRootQueryPage, MotionRootQueryVariables>(MotionRoot, {
    variables,
  })
  const { document, viewer = null, persistentAlertBannerViewer = null } = data?.root || {}
  const { ref: dispatcherRef, sendMetrics, sendCopyEvents } = useMetrics<HTMLDivElement>()
  const { sendGA4Event } = useGA4Dispatcher()

  const hasPaidPlan = !!viewer?.hasPaidPlan

  useConsumeCredits(shouldConsumeViewCredits, document)
  useDwellTime(document, sendMetrics, metricsProperties)

  // This hook should be run only once per page, that's why docId should be its only dependency
  useEffect(() => {
    sendMetrics('Doc.Viewed', metricsProperties)

    hotjarEvent('event', 'ExperimentMotionFarolRollout')

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.docId])

  if (httpError || !document) return null

  const handleCopy = () => {
    sendCopyEvents(metricsProperties)
  }

  const handleDocumentDownloadFrom =
    (componentClass: COMPONENT_CLASS) => (successfulAttempt: boolean) => {
      sendGA4Event(
        new GA4FeatureUseEvent({
          featureClass: FEATURE_CLASS.DOCUMENT_DOWNLOAD,
          featureName: FEATURE_NAME.DOWNLOAD_MOTION_CONTENT,
          selectedOption: SELECTED_OPTION.DOWNLOAD_BUTTON,
          resourceId: document.boxResourceId,
          requiredUserStatus: REQUIRED_USER_STATUS.SUBSCRIBER_USER,
          componentClass,
          successfulAttempt,
        })
      )
    }

  const { canCopyContent, viewerCanAccessFullDocument } = document
  const enableActions = !!viewerCanAccessFullDocument

  return (
    <AppBar.Provider>
      <ServerSidePersistentAlertBanner persistentAlertBannerViewer={persistentAlertBannerViewer} />
      <Docview
        document={document}
        viewer={viewer}
        handleCopy={handleCopy}
        metricsProperties={metricsProperties}
        canCopyContent={!!canCopyContent}
        dispatcherRef={dispatcherRef}
        contentRef={dispatcherRef}
        disableTouchFeatures={!!disableTouchFeatures}
        topbarCurrentNavKey="PECAS"
        metaTags={<MotionMetaTags document={document} />}
        afterTopbar={
          hasPaidPlan &&
          enableActions && (
            <DocSearch
              contentRef={contentRef}
              ref={docSearchRef}
              slug={document.slug || undefined}
            />
          )
        }
        header={
          <>
            <MotionHeader
              document={document}
              toolbar={
                <MotionToolbar
                  document={document}
                  viewer={viewer}
                  docSearchRef={docSearchRef}
                  docContentRef={contentRef}
                  ga4ComponentClass={COMPONENT_CLASS.DOCUMENT_HEADER}
                  onDocumentDownload={handleDocumentDownloadFrom(COMPONENT_CLASS.DOCUMENT_HEADER)}
                  jusiaVariant={jusiaVariant}
                />
              }
            />
            <AppBar.StickyTrigger />
          </>
        }
        asideContent={<MotionSidebar document={document} viewer={viewer} contentRef={contentRef} />}
        mainContent={
          <ViewLockContext
            viewer={viewer}
            document={document}
            enableLawyerLock={!enableActions}
            enableOabLock={!enableActions}
            featureContext={{
              featureCode: FEATURE_CODES.VIEW_LR_CONTENT,
              canUseFeature: enableActions,
            }}
          >
            <ViewLockWrapper
              document={document}
              viewer={viewer}
              eventData={eventData}
              maxHeight="140px"
              isGooglebotAgent={isGoogleBotAgent}
              loginWallProps={{
                title: 'Este conteúdo é exclusivo a profissionais advogados com OAB verificada',
                subtitle: 'Para continuar utilizando:',
              }}
            >
              <MotionDocument
                document={document}
                viewer={viewer}
                processedText={processedText}
                contentRef={contentRef}
              />
            </ViewLockWrapper>
          </ViewLockContext>
        }
        relatedContent={
          <div className={classNames(styles.relatedContent, printStyles.unprintable)}>
            <RecommendedDocuments document={document} />
            <LawsuitSubjects document={document} />
            <MotionBreadcrumb document={document} />
          </div>
        }
        endContent={
          <MotionAppbar
            document={document}
            className={printStyles.unprintable}
            toolbar={
              <MotionToolbar
                document={document}
                viewer={viewer}
                docSearchRef={docSearchRef}
                docContentRef={contentRef}
                ga4ComponentClass={COMPONENT_CLASS.DOCUMENT_TOPBAR}
                onDocumentDownload={handleDocumentDownloadFrom(COMPONENT_CLASS.DOCUMENT_TOPBAR)}
                jusiaVariant={jusiaVariant}
              />
            }
          />
        }
      />

      <MotionTooltip contentRef={contentRef} />
      <HotjarScript hjid={JUSBRASIL_HOTJAR_SITE_ID} />
    </AppBar.Provider>
  )
}

const getServerSideProps: GetServerSideProps<MotionPageProps> = async (ctx) => {
  const { lawsuitId, slugWithId } = ctx.params ?? {}
  if (!Number(lawsuitId) || !slugWithId) {
    return { notFound: true }
  }

  const parts = extractMotionUrl(Array.isArray(slugWithId) ? slugWithId[0] : slugWithId)
  if (!parts || !parts.docId) {
    return {
      notFound: true,
    }
  }

  const variables: MotionRootQueryVariables = {
    docId: parts.docId,
  }
  const { client, withApolloProps } = withApollo(ctx)
  const { data } = await client.query<MotionRootQuery>({
    query: MotionRoot,
    variables,
  })
  const { document, viewer } = data?.root ?? {}
  if (!document) {
    return {
      notFound: true,
    }
  }

  if (document.status === DocumentStatus.REMOVIDO || !document.isActive) {
    return {
      redirect: {
        destination: '/pecas',
        permanent: false,
      },
    }
  }

  const experimentContext = await buildExperimentContext(ctx, getActiveExperiments())
  const metricsProperties = buildMetricsProperties(ctx, document, viewer, experimentContext)

  const { client: growthBookClient } = await withGrowthBook(ctx)
  const jusiaVariant = growthBookClient.getFeatureValue(
    DOCVIEW_MOTION_JUSIA_FLAG.FEATURE_FLAG,
    ''
  ) as JusIaVariant
  growthBookClient.destroy()

  const status = getDocumentStatusCode(ctx.req, document, viewer)
  if (status !== 200) {
    ctx.res.statusCode = status
    return {
      props: {
        variables,
        metricsProperties,
        httpError: true,
      },
    }
  }

  const redirect = document.url && redirectDocumentUrl(ctx, document.url)
  if (redirect) {
    return { redirect }
  }

  const ua = UAParser(ctx.req.headers['user-agent'])
  const disableTouchFeatures = ua.device.type === 'mobile' || ua.device.type === 'tablet'

  return {
    props: {
      ...withApolloProps(),
      ...withTopbarProps(ctx),
      ...withUserSession(ctx),
      variables,
      metricsProperties,
      disableTouchFeatures,
      shouldConsumeViewCredits: shouldConsumeViewCredits(ctx.req, viewer),
      isGooglebotAgent: isGoogleBotAgent(ctx.req),
      persistentAlertBannerViewer: data?.root?.persistentAlertBannerViewer,
      processedText: new MotionTextProcessor(
        document.text || '',
        document.lawsuitMotionTheme
      ).processedText(),
      jusiaVariant,
    },
  }
}

export const MotionPageExports = { getServerSideProps }
